import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { publicApi } from "../../api";
import SEO from "../../components/SEO";
import {
  ArrowRight,
  Star,
  Play,
  Zap,
  Shield,
  TrendingUp,
} from "lucide-react";

const PublicHomePage = () => {
  const [niches, setNiches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNiches = async () => {
      try {
        const res = await publicApi.getNiches();
        setNiches(res.data);
      } catch (error) {
        console.error("Failed to load niches:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchNiches();
  }, []);

  const features = [
    {
      icon: Star,
      title: "Unbiased Reviews",
      description: "Real analysis based on hands-on testing and user feedback",
    },
    {
      icon: Zap,
      title: "AI-Powered Insights",
      description: "Constantly updated comparisons using latest market data",
    },
    {
      icon: Shield,
      title: "Trusted Recommendations",
      description: "Transparent affiliate relationships, honest opinions",
    },
  ];

  return (
    <div data-testid="public-homepage">
      <SEO
        title="Honest Software Reviews & Comparisons"
        description="Find the best software tools with unbiased reviews, detailed comparisons, and data-driven recommendations. Compare AI tools, SEO software, e-commerce platforms and more."
        url="/"
        keywords="software reviews, software comparison, best software tools, AI tools, SEO software, e-commerce platforms"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1660165458059-57cfb6cc87e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxmdXR1cmlzdGljJTIwYXJ0aWZpY2lhbCUyMGludGVsbGlnZW5jZSUyMHRlY2hub2xvZ3klMjBhYnN0cmFjdHxlbnwwfHx8fDE3Njk4MDc2MDZ8MA&ixlib=rb-4.1.0&q=85')] opacity-10 bg-cover bg-center" />
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 py-20 relative">
          <div className="max-w-2xl">
            <span className="inline-block text-blue-400 text-sm font-semibold tracking-wider uppercase mb-4">
              Software Reviews You Can Trust
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight font-serif">
              Find the Perfect
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                Software for Your Needs
              </span>
            </h1>
            <p className="text-lg text-slate-300 mt-5 leading-relaxed">
              In-depth reviews, honest comparisons, and data-driven
              recommendations to help you make the right choice.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link
                to="/ai-video-tools"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                data-testid="explore-ai-video-btn"
              >
                <Play size={18} />
                Explore AI Video Tools
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-14 bg-white">
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-slate-900 font-serif">
              Why Trust Our Reviews?
            </h2>
            <p className="text-slate-600 mt-3">
              We combine hands-on testing, user feedback, and AI-powered
              analysis to bring you the most comprehensive software reviews.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="p-6 rounded-xl border border-slate-200 hover:border-blue-200 hover:shadow-lg transition-all group"
                >
                  <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
                    <Icon size={24} className="text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Niches Section */}
      <section className="py-16 bg-slate-50">
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-slate-900 font-serif">
              Explore Categories
            </h2>
            <p className="text-slate-600 mt-2">
              Find the best tools in each category
            </p>
          </div>

          {loading ? (
            <div className="text-center text-slate-500">Loading...</div>
          ) : niches.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {niches.map((niche) => (
                <Link
                  key={niche.id}
                  to={`/${niche.slug}`}
                  className="group relative overflow-hidden rounded-xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all"
                  data-testid={`niche-card-${niche.slug}`}
                >
                  <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 relative overflow-hidden">
                    {niche.hero_image_url ? (
                      <img
                        src={niche.hero_image_url}
                        alt={niche.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Play size={48} className="text-slate-300" />
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider">
                      {niche.category}
                    </span>
                    <h3 className="text-xl font-bold text-slate-900 mt-2 group-hover:text-blue-600 transition-colors font-serif">
                      {niche.name}
                    </h3>
                    <p className="text-slate-600 text-sm mt-2 line-clamp-2">
                      {niche.description}
                    </p>
                    <div className="flex items-center gap-2 mt-4 text-blue-600 font-medium text-sm">
                      <span>View Comparison</span>
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-slate-500 mb-4">No categories available yet.</p>
              <Link
                to="/admin/niches"
                className="text-blue-600 hover:underline"
              >
                Add your first niche in the admin dashboard
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white font-serif">
            Ready to Find Your Perfect Tool?
          </h2>
          <p className="text-slate-300 mt-4 text-lg">
            Browse our comprehensive comparisons and make an informed decision.
          </p>
          <Link
            to="/ai-video-tools"
            className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors mt-8"
            data-testid="cta-explore-btn"
          >
            Start Exploring
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PublicHomePage;
